import React, { useContext, FC } from 'react';
import { ThemeContext } from 'styled-components';
import { Theme, useMediaQuery, aboveBreakpoint } from '@bechtel/gds';
import {
  FEDashboardStatStyled,
  FEDashboardContentStyled,
  SectionListStyled,
  DashBoardLeftSectionStyled,
  StatBlockGridFEStyled,
  WorkPackageContainerStyled,
  PendingApprovalListContainerStyled,
} from './styled';
import { WorkpackageList } from '~/workpackage-list';
import { PendingApprovalList } from './pending-approval-list/internals';
import {
  CrewMember,
  WorkPackageStats,
  WorkPackageStatsInput,
  WorkPackageFilterByTerm,
  MilestoneFilterStatusFlag,
} from '@bechtel/types';
import { get } from 'lodash';
import { dashBoardStatsMock } from '~/form-data';
import { useSessionStorage, CacheKeys, useAppUser, useModulePermission } from '@bechtel/client-common';
import { DrawerValuesProps, drawerValues as defaultValues } from '@bechtel/client-work-packages';
import { StatBlockStyled } from '~/styled';
import { CommittedMilestoneStatsMock } from '~/superintendent/form-data';
import { isWorkPackagePersona, getStatBlockGridData } from '~/utilities';
import { LazyQueryResult } from '@apollo/client';
import { defaultValues as blockDefaultValues, BlockDrawerValuesProps } from '@bechtel/client-blocks';
import { useNavigate } from 'react-router-dom';
import { t } from '@lingui/macro';
import { Carousel } from '@bechtel/client-dashboard-common';

interface FieldEngineerDashboardContentProps {
  crewMember: CrewMember | null | undefined;
  result: LazyQueryResult<{ workPackageStats: WorkPackageStats }, { input: WorkPackageStatsInput }>;
}

export const FieldEngineerDashboardContent: FC<FieldEngineerDashboardContentProps> = ({ crewMember, result }) => {
  const { canViewBlockModule, canViewWorkPackageModule } = useModulePermission();
  const navigate = useNavigate();
  const { user } = useAppUser();
  const { breakpoints } = useContext<Theme>(ThemeContext);
  const isWideScreen = useMediaQuery(aboveBreakpoint(breakpoints.widescreen));

  const setDrawerValues = useSessionStorage<DrawerValuesProps>(CacheKeys.WorkPackageDrawerValues)[1];
  const setCursor = useSessionStorage<number>(CacheKeys.WorkPackageListCursor)[1];
  const setBlockDrawerValues = useSessionStorage<BlockDrawerValuesProps>(CacheKeys.BlockDrawerValues)[1];
  const { called, loading, data } = result;

  const fieldEngineerDashboardStats =
    crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)
      ? get(data, 'workPackageStats', dashBoardStatsMock)
      : dashBoardStatsMock;
  const {
    numWorkPackages,
    numBlocks,
    numRejects,
    plannedMilestones,
    committedMilestones,
    earnedMilestones,
    approvedMilestones,
  } = fieldEngineerDashboardStats;
  const plannedMilestoneStats = getStatBlockGridData(
    plannedMilestones,
    committedMilestones ? committedMilestones : CommittedMilestoneStatsMock
  );
  const claimedMilestoneStats = getStatBlockGridData(earnedMilestones);
  const ApprovedMilestoneStats = getStatBlockGridData(approvedMilestones);
  const statsLoading =
    (!called && crewMember && crewMember.persona && isWorkPackagePersona(crewMember?.persona)) || loading;

  const navigateToWorkPackage = async (values: DrawerValuesProps) => {
    if (user && user.crewMember && !isWorkPackagePersona(user.crewMember.persona)) {
      //TODO: Enable filter by owners in drawer
      // const filterByOwners = {
      //   persona: crewMember.persona,
      //   crewMemberId: crewMember.id,
      // };
      values = { ...values, searchScope: false };
    }
    setCursor(1);
    await setDrawerValues(values);
    navigate('work-packages');
  };

  return (
    <FEDashboardContentStyled>
      <FEDashboardStatStyled>
        <StatBlockStyled
          value={String(numWorkPackages)}
          label={t`Work Packages`}
          icon="briefcase"
          style={{ cursor: canViewWorkPackageModule && numWorkPackages > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewWorkPackageModule && numWorkPackages > 0) {
              navigateToWorkPackage({ ...defaultValues, filterBy: WorkPackageFilterByTerm.ALL });
            }
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numBlocks)}
          label={t`Blocks`}
          icon="exclamation-triangle"
          style={{ cursor: canViewBlockModule && numBlocks > 0 ? `pointer` : `default` }}
          onClick={async () => {
            if (canViewBlockModule && numBlocks > 0) {
              await setBlockDrawerValues(blockDefaultValues);
              navigate('blocks');
            }
          }}
          loading={statsLoading}
        />
        <StatBlockStyled
          value={String(numRejects)}
          label={t`Rejections`}
          icon="comment-alt-times"
          onClick={() => {
            navigateToWorkPackage({ ...defaultValues, filterBy: MilestoneFilterStatusFlag.REJECTED });
          }}
          loading={statsLoading}
        />
        <StatBlockGridFEStyled
          data={plannedMilestoneStats}
          label={t`Planned`}
          subLabel={t`Committed`}
          loading={statsLoading}
        />
        <StatBlockGridFEStyled data={claimedMilestoneStats} label={t`Claimed`} loading={statsLoading} />
        <StatBlockGridFEStyled data={ApprovedMilestoneStats} label={t`Approved`} loading={statsLoading} />
      </FEDashboardStatStyled>
      {isWideScreen ? (
        <SectionListStyled>
          <WorkPackageContainerStyled>
            <DashBoardLeftSectionStyled>
              <WorkpackageList crewMember={crewMember} />
            </DashBoardLeftSectionStyled>
          </WorkPackageContainerStyled>
          <PendingApprovalListContainerStyled>
            <PendingApprovalList crewMember={crewMember} />
          </PendingApprovalListContainerStyled>
        </SectionListStyled>
      ) : (
        <Carousel>
          <WorkpackageList crewMember={crewMember} />
          <PendingApprovalList crewMember={crewMember} />
        </Carousel>
      )}
    </FEDashboardContentStyled>
  );
};
